import { useEffect, useState } from "react";
import ComplianceNotice from "@/components/ComplianceNotice";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, Plus, Trash2, Download, FileText, Receipt, Wallet, ArrowLeftRight, RotateCcw, ClipboardList, Upload, Pencil } from "lucide-react";

const TABS = [
  { v: "contributions", label: "Fundraising", icon: Wallet, summary: "Incoming donations & contributions" },
  { v: "expenses", label: "Campaign Spending", icon: Receipt, summary: "Individual spend items" },
  { v: "budgets", label: "Budget", icon: ClipboardList, summary: "Category-level allocations" },
  { v: "transactions", label: "Account Transactions", icon: ArrowLeftRight, summary: "Bank-style ledger" },
  { v: "reimbursements", label: "Reimbursements", icon: RotateCcw, summary: "Outstanding & paid" },
  { v: "reports", label: "Report Builder", icon: FileText, summary: "Named filing-period tabs" },
];

const DONATION_TYPES = ["WinRed", "Check", "Cash", "Electronic Deposit", "In-Kind", "Other"];
const EXPENSE_CATEGORIES = ["Mailings", "Yard Signs", "Swag", "Technology", "Events", "Printed Materials", "Consulting", "Travel", "Food", "Office", "Other"];
const TX_TYPES = ["Electronic Deposit", "Check Deposit", "Cash Deposit", "Debit Card", "Check Dispersed"];
const REIMB_STATUSES = ["In Progress", "Done"];

// Field configs drive both manual-entry forms and the table view, keeping
// the per-entity UI declarative. Each entity is one entry.
const ENTITY_CONFIG = {
  contributions: {
    fields: [
      { k: "date_deposited", label: "Date deposited", type: "date" },
      { k: "donor_name", label: "Donor name", type: "text", req: true },
      { k: "gross", label: "Gross $", type: "money" },
      { k: "net", label: "Net $", type: "money" },
      { k: "donation_type", label: "Type", type: "select", options: DONATION_TYPES },
      { k: "address", label: "Address", type: "text" },
      { k: "city", label: "City", type: "text" },
      { k: "state", label: "State", type: "text" },
      { k: "zip", label: "Zip", type: "text" },
      { k: "email", label: "Email", type: "text" },
      { k: "phone", label: "Phone", type: "text" },
    ],
    table: ["date_deposited", "donor_name", "donation_type", "gross", "net", "email"],
    totals: ["gross", "net"],
    reportable: true,
  },
  expenses: {
    fields: [
      { k: "date", label: "Date", type: "date" },
      { k: "budget_item", label: "Budget item", type: "text", req: true },
      { k: "amount", label: "Amount $", type: "money" },
      { k: "vendor", label: "Vendor", type: "text" },
      { k: "category", label: "Category", type: "select", options: EXPENSE_CATEGORIES },
      { k: "description", label: "Description", type: "textarea" },
      { k: "invoiced", label: "Invoiced", type: "bool" },
      { k: "paid_via_campaign_account", label: "Paid via campaign account", type: "bool" },
    ],
    table: ["date", "budget_item", "category", "vendor", "amount"],
    totals: ["amount"],
    reportable: true,
  },
  budgets: {
    fields: [
      { k: "category", label: "Category", type: "select", options: EXPENSE_CATEGORIES, req: true },
      { k: "total_budget", label: "Total budget $", type: "money" },
      { k: "notes", label: "Notes", type: "textarea" },
    ],
    table: ["category", "total_budget"],
    totals: ["total_budget"],
    reportable: false,
  },
  transactions: {
    fields: [
      { k: "date_posted", label: "Date posted", type: "date" },
      { k: "transaction_type", label: "Type", type: "select", options: TX_TYPES },
      { k: "description", label: "Description", type: "text" },
      { k: "amount", label: "Amount $ (negative = withdrawal)", type: "money" },
      { k: "status", label: "Status", type: "text" },
    ],
    table: ["date_posted", "transaction_type", "description", "amount", "status"],
    totals: ["amount"],
    reportable: false,
  },
  reimbursements: {
    fields: [
      { k: "name", label: "Recipient", type: "text", req: true },
      { k: "amount", label: "Amount $", type: "money" },
      { k: "description", label: "Description", type: "textarea" },
      { k: "status", label: "Status", type: "select", options: REIMB_STATUSES },
    ],
    table: ["name", "amount", "status", "description"],
    totals: ["amount"],
    reportable: false,
  },
};

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export default function Finances() {
  const [tab, setTab] = useState("contributions");
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-10" data-testid="finances-root">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-flag-red flex items-center gap-2">
            Finances <span data-testid="finances-pro-badge" className="bg-flag-red text-white text-[9px] px-1.5 py-0.5 rounded">PRO</span>
          </p>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-black text-flag-navy">Your campaign books</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">Track contributions, spending, transactions, and reimbursements in one place. Built for your record-keeping, not for filing.</p>
        </div>
      </div>

      <ComplianceNotice kind="finance" className="mt-5" />

      <div
        role="alert"
        data-testid="finances-disclaimer"
        className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 leading-relaxed"
      >
        <div className="flex gap-2 items-start">
          <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>For record-keeping only.</strong> Electable Finances helps you organize what came in,
            what went out, and what's in your reports. It does <strong>not</strong> generate FEC or state
            campaign-finance filings, calculate contribution limits, or determine what's legally reportable.
            All compliance, reporting, and filings must be confirmed with your campaign&rsquo;s treasurer and the
            FEC / your state board of elections.
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-1 flex-wrap" data-testid="finances-tabs">
        {TABS.map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            type="button"
            onClick={() => setTab(v)}
            data-testid={`finances-tab-${v}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md transition-colors ${tab === v ? "bg-flag-navy text-white" : "text-slate-700 bg-slate-100 hover:bg-slate-200"}`}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "reports" ? <ReportsPane /> : <EntityPane entity={tab} />}
      </div>
    </div>
  );
}

/* ----- One pane per simple entity (contributions, expenses, budgets, transactions, reimbursements) ----- */
function EntityPane({ entity }) {
  const cfg = ENTITY_CONFIG[entity];
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [reports, setReports] = useState([]);
  // For Budget rollup, we need expenses too (sum by category).
  const [expenseSpentByCat, setExpenseSpentByCat] = useState({});
  const tabLabel = TABS.find((t) => t.v === entity)?.label || entity;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/finances/${entity}`);
      setRows(data.rows || []);
      setTotals(data.totals || {});
      // Budget rollup: also fetch expenses to compute per-category "spent".
      if (entity === "budgets") {
        try {
          const { data: e } = await api.get("/finances/expenses");
          const by = {};
          for (const r of (e.rows || [])) {
            const k = (r.category || "").trim();
            by[k] = (by[k] || 0) + Number(r.amount || 0);
          }
          setExpenseSpentByCat(by);
        } catch (breakdownErr) {
          // Non-blocking: the per-category spend rollup is a nice-to-have
          // on top of the main finances load. Log for triage without
          // toast-spamming the user, since totals still render below.
          console.warn("[Finances] category rollup failed", breakdownErr);
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  // Refetch when the tab (entity) changes. `load`/`cfg` both derive from
  // `entity`, so entity is the one true trigger — listing them would only
  // add churn without changing behavior.
  useEffect(() => {
    load();
    if (cfg.reportable) api.get("/finances/reports").then(({ data }) => setReports(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const onDelete = async (id) => {
    try {
      await api.delete(`/finances/${entity}/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleReport = async (row, reportId) => {
    const next = new Set(row.report_ids || []);
    if (next.has(reportId)) next.delete(reportId); else next.add(reportId);
    try {
      await api.post(`/finances/${entity}/${row.id}/reports`, { report_ids: [...next] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-xl font-bold text-flag-navy">{tabLabel}</h2>
        <div className="flex items-center gap-2">
          <Button onClick={() => setUploadOpen(true)} variant="outline" data-testid={`finances-${entity}-upload`} className="border-2 font-semibold">
            <Upload className="h-4 w-4 mr-1" /> Upload CSV
          </Button>
          <Button onClick={() => setAddOpen(true)} data-testid={`finances-${entity}-add`} className="bg-flag-red hover:bg-flag-red-hover text-white font-bold">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Totals strip */}
      {cfg.totals.length > 0 && (
        <div className="bg-flag-navy text-white rounded-xl px-5 py-3 inline-flex items-center gap-6 flex-wrap" data-testid={`finances-${entity}-totals`}>
          {cfg.totals.map((k) => {
            const lbl = (cfg.fields.find((f) => f.k === k)?.label || k).replace(/\s*\$\s*$/, "");
            return (
              <div key={k}>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">Total {lbl}</div>
                <div className="font-display text-2xl font-black">{fmtMoney(totals[k])}</div>
              </div>
            );
          })}
          <div className="text-xs opacity-75 border-l border-white/20 pl-6">{rows.length} record{rows.length === 1 ? "" : "s"}</div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`finances-${entity}-table`}>
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                {cfg.table.map((k) => (
                  <th key={k} className="text-left px-3 py-2 font-bold">
                    {cfg.fields.find((f) => f.k === k)?.label || k}
                  </th>
                ))}
                {entity === "budgets" && <th className="text-left px-3 py-2 font-bold">Spent</th>}
                {entity === "budgets" && <th className="text-left px-3 py-2 font-bold">Remaining</th>}
                {entity === "transactions" && <th className="text-left px-3 py-2 font-bold">Running balance</th>}
                {cfg.reportable && <th className="text-left px-3 py-2 font-bold">Reports</th>}
                <th className="text-right px-3 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={cfg.table.length + 4} className="text-center py-6 text-slate-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={cfg.table.length + 4} className="text-center py-10 text-slate-500">No records yet. Click <strong>Add</strong> or <strong>Upload CSV</strong> to start.</td></tr>}
              {!loading && computeRollups(entity, rows, expenseSpentByCat).map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`finances-${entity}-row-${r.id}`}>
                  {cfg.table.map((k) => {
                    const fdef = cfg.fields.find((f) => f.k === k);
                    let val = r[k];
                    if (fdef?.type === "money") val = fmtMoney(val);
                    return <td key={k} className="px-3 py-2 text-slate-800">{val == null || val === "" ? "—" : String(val)}</td>;
                  })}
                  {entity === "budgets" && <td className="px-3 py-2 text-slate-800">{fmtMoney(r._spent)}</td>}
                  {entity === "budgets" && <td className={`px-3 py-2 font-semibold ${r._remaining < 0 ? "text-flag-red" : "text-emerald-700"}`} data-testid={`finances-budget-remaining-${r.id}`}>{fmtMoney(r._remaining)}</td>}
                  {entity === "transactions" && <td className="px-3 py-2 font-semibold text-flag-navy" data-testid={`finances-tx-balance-${r.id}`}>{fmtMoney(r._balance)}</td>}
                  {cfg.reportable && (
                    <td className="px-3 py-2">
                      <ReportAssignButton row={r} reports={reports} onToggle={(rid) => toggleReport(r, rid)} entity={entity} />
                    </td>
                  )}
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" onClick={() => setEditRow(r)} data-testid={`finances-${entity}-edit-${r.id}`} className="h-8 text-xs text-slate-500 hover:text-flag-navy">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" onClick={() => onDelete(r.id)} data-testid={`finances-${entity}-del-${r.id}`} className="h-8 text-xs text-slate-500 hover:text-flag-red">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen && (
        <RecordDialog
          entity={entity}
          cfg={cfg}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
      {editRow && (
        <RecordDialog
          entity={entity}
          cfg={cfg}
          initial={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); load(); }}
        />
      )}
      {uploadOpen && (
        <CsvUploadDialog
          entity={entity}
          cfg={cfg}
          onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// Compute derived rollup columns per entity. Pure function — kept outside the
// component so it's easy to verify in isolation and avoids re-allocating
// per render via useMemo overkill.
function computeRollups(entity, rows, expenseSpentByCat) {
  if (entity === "budgets") {
    return rows.map((r) => {
      const spent = expenseSpentByCat[(r.category || "").trim()] || 0;
      return { ...r, _spent: spent, _remaining: Number(r.total_budget || 0) - spent };
    });
  }
  if (entity === "transactions") {
    // Sort ascending by date_posted (then created_at as tiebreaker) and
    // accumulate. We render in chronological order so the running balance
    // increases naturally down the table.
    const sorted = [...rows].sort((a, b) => {
      const da = (a.date_posted || "");
      const db = (b.date_posted || "");
      if (da !== db) return da < db ? -1 : 1;
      return (a.created_at || "") < (b.created_at || "") ? -1 : 1;
    });
    let bal = 0;
    return sorted.map((r) => { bal += Number(r.amount || 0); return { ...r, _balance: bal }; });
  }
  return rows;
}

function ReportAssignButton({ row, reports, onToggle, entity }) {
  const [open, setOpen] = useState(false);
  const assigned = new Set(row.report_ids || []);
  return (
    <div className="relative">
      <Button
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        data-testid={`finances-${entity}-assign-${row.id}`}
        className="h-7 px-2 text-xs font-semibold border"
      >
        <Plus className="h-3 w-3 mr-1" /> {assigned.size > 0 ? `${assigned.size} report${assigned.size === 1 ? "" : "s"}` : "Add to report"}
      </Button>
      {open && (
        <div className="absolute z-10 mt-1 right-0 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-2 max-h-64 overflow-y-auto" data-testid={`finances-${entity}-assign-menu-${row.id}`}>
          {reports.length === 0 && <p className="text-xs text-slate-500 p-2">No reports yet. Create one in the Report Builder tab.</p>}
          {reports.map((rp) => (
            <button
              key={rp.id}
              type="button"
              onClick={() => onToggle(rp.id)}
              className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 ${assigned.has(rp.id) ? "font-bold text-flag-navy" : "text-slate-700"}`}
            >
              <input type="checkbox" checked={assigned.has(rp.id)} readOnly className="pointer-events-none" />
              {rp.name}
            </button>
          ))}
          <button type="button" onClick={() => setOpen(false)} className="w-full text-center text-xs mt-2 text-slate-500 hover:text-flag-navy">Close</button>
        </div>
      )}
    </div>
  );
}

function RecordDialog({ entity, cfg, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  // Seed form with only fields known to this entity, coercing bools cleanly.
  const seed = () => {
    if (!initial) return {};
    const out = {};
    for (const f of cfg.fields) {
      const v = initial[f.k];
      if (v === undefined || v === null) continue;
      out[f.k] = f.type === "bool" ? !!v : v;
    }
    return out;
  };
  const [form, setForm] = useState(seed);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    const required = cfg.fields.filter((f) => f.req);
    for (const f of required) {
      if (!form[f.k]) { toast.error(`${f.label} is required`); return; }
    }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/finances/${entity}/${initial.id}`, { data: form });
        toast.success("Updated");
      } else {
        await api.post(`/finances/${entity}`, { data: form });
        toast.success("Saved");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  const tabLabel = TABS.find((t) => t.v === entity)?.label?.toLowerCase() || entity;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid={`finances-${isEdit ? "edit" : "add"}-dialog-${entity}`}>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit" : "Add"} {tabLabel} record</DialogTitle>
          <DialogDescription>For your books. Verify reportability with your treasurer.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
          {cfg.fields.map((f) => (
            <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                {f.label}{f.req && <span className="text-flag-red ml-1">*</span>}
              </Label>
              {(() => {
                // Field-type dispatch as if/returns — one control per type.
                if (f.type === "select") return (
                <Select value={form[f.k] || "__none"} onValueChange={(v) => set(f.k, v === "__none" ? "" : v)}>
                  <SelectTrigger data-testid={`finances-field-${entity}-${f.k}`} className="mt-1 h-10"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                );
                if (f.type === "textarea") return (
                <Textarea data-testid={`finances-field-${entity}-${f.k}`} value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} rows={3} className="mt-1" />
                );
                if (f.type === "bool") return (
                <label className="inline-flex items-center gap-2 mt-2 text-sm">
                  <input type="checkbox" data-testid={`finances-field-${entity}-${f.k}`} checked={!!form[f.k]} onChange={(e) => set(f.k, e.target.checked)} />
                  Yes
                </label>
                );
                let inputType = "text";
                if (f.type === "money") inputType = "number";
                else if (f.type === "date") inputType = "date";
                return (
                <Input data-testid={`finances-field-${entity}-${f.k}`} type={inputType} value={form[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} className="mt-1 h-10" />
                );
              })()}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid={`finances-${isEdit ? "edit" : "add"}-cancel-${entity}`}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid={`finances-${isEdit ? "edit" : "add"}-submit-${entity}`} className="bg-flag-red hover:bg-flag-red-hover text-white font-bold">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Save record")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----- Report Builder pane ----- */
function ReportsPane() {
  const [reports, setReports] = useState([]);
  const [name, setName] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/finances/reports");
      setReports(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await api.post("/finances/reports", { name: n });
      setName("");
      load();
      toast.success("Report created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openReport = async (r) => {
    setActive(r);
    try {
      const { data } = await api.get(`/finances/reports/${r.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the report "${r.name}"? Records stay; only this report tab is removed.`)) return;
    try {
      await api.delete(`/finances/reports/${r.id}`);
      if (active?.id === r.id) { setActive(null); setDetail(null); }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const downloadExport = async (r) => {
    try {
      // Feb 2026 auth migration: session lives in an HttpOnly cookie, so we
      // just pass `credentials: "include"` and the browser sends it. No
      // more Authorization: Bearer / localStorage token read here.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/finances/reports/${r.id}/export`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `electable-${r.name.replace(/[^a-z0-9]+/gi, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="finances-reports-list">
        <h3 className="font-display font-bold text-flag-navy">Your reports</h3>
        <p className="text-xs text-slate-500">Name a tab per filing period (e.g. &ldquo;32-Day Pre-General Report&rdquo;), then assign contributions and expenses to it from the other tabs.</p>
        <div className="flex items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New report name" data-testid="finances-report-name" className="h-9" />
          <Button onClick={create} data-testid="finances-report-create" className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-9">Create</Button>
        </div>
        <ul className="space-y-1 pt-2">
          {reports.length === 0 && <li className="text-sm text-slate-500 italic">No reports yet.</li>}
          {reports.map((r) => (
            <li key={r.id} className={`flex items-center justify-between px-2 py-1.5 rounded ${active?.id === r.id ? "bg-flag-navy text-white" : "hover:bg-slate-100"}`}>
              <button type="button" onClick={() => openReport(r)} data-testid={`finances-report-open-${r.id}`} className="text-left text-sm font-semibold flex-1 truncate">{r.name}</button>
              <button type="button" onClick={() => downloadExport(r)} title="Export CSV" data-testid={`finances-report-export-${r.id}`} className="p-1 hover:text-flag-red"><Download className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => remove(r)} title="Delete report" data-testid={`finances-report-del-${r.id}`} className="p-1 hover:text-flag-red"><Trash2 className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4" data-testid="finances-report-detail">
        {(() => {
          // Three states as if/returns: no selection → loading → detail.
          if (!active) return (
          <div className="text-center py-16 text-slate-500">
            <FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" />
            Select a report on the left to see its assigned transactions.
          </div>
          );
          if (!detail) return <p className="text-slate-500">Loading…</p>;
          return (
          <div className="space-y-4">
            <div>
              <h3 className="font-display text-xl font-bold text-flag-navy">{detail.report.name}</h3>
              <p className="text-xs text-slate-500">For your records. Verify totals with your treasurer before filing.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Gross in" value={fmtMoney(detail.totals.gross)} />
              <Stat label="Net in" value={fmtMoney(detail.totals.net)} />
              <Stat label="Expenses" value={fmtMoney(detail.totals.expenses)} />
            </div>
            <Section title={`Contributions (${detail.contributions.length})`}>
              {detail.contributions.length === 0 ? <p className="text-sm text-slate-500">None assigned.</p> : (
                <ul className="divide-y divide-slate-100 text-sm">
                  {detail.contributions.map((c) => (
                    <li key={c.id} className="py-1.5 flex justify-between"><span><strong>{c.donor_name}</strong> · {c.date_deposited} · {c.donation_type}</span><span className="font-semibold">{fmtMoney(c.gross)}</span></li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title={`Expenses (${detail.expenses.length})`}>
              {detail.expenses.length === 0 ? <p className="text-sm text-slate-500">None assigned.</p> : (
                <ul className="divide-y divide-slate-100 text-sm">
                  {detail.expenses.map((e) => (
                    <li key={e.id} className="py-1.5 flex justify-between"><span><strong>{e.vendor || e.budget_item}</strong> · {e.date} · {e.category}</span><span className="font-semibold">-{fmtMoney(e.amount)}</span></li>
                  ))}
                </ul>
              )}
            </Section>
            <Button onClick={() => downloadExport(active)} data-testid="finances-report-export-active" className="bg-flag-navy hover:bg-flag-navy/90 text-white font-bold">
              <Download className="h-4 w-4 mr-1.5" /> Export this report as CSV
            </Button>
            <ComplianceNotice kind="finance" className="mt-3" />
          </div>
          );
        })()}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-display text-lg font-black text-flag-navy">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="font-display text-sm font-bold text-flag-navy uppercase tracking-wider">{title}</h4>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/* ---- CSV upload + column mapping (mirrors the Voter List pattern) ---- */
function CsvUploadDialog({ entity, cfg, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [parse, setParse] = useState(null);  // { columns, sample, suggested, fields }
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);

  const tabLabel = TABS.find((t) => t.v === entity)?.label || entity;

  const onPick = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/finances/${entity}/parse-headers`, fd);
      setParse(data);
      setMapping(data.suggested || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("plan_json", JSON.stringify({ mapping, mode }));
      const { data } = await api.post(`/finances/${entity}/import`, fd);
      toast.success(`Imported ${data.added} record${data.added === 1 ? "" : "s"} (${data.mode}).`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Allowed mapping targets — built from the entity's field list (excluding
  // computed/report-only fields). Used to render one Select per target field.
  const targetFields = cfg.fields.filter((f) => f.k !== "report_ids" && f.k !== "notes" && f.k !== "invoiced" && f.k !== "paid_via_campaign_account");

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid={`finances-upload-dialog-${entity}`}>
        <DialogHeader>
          <DialogTitle>Upload {tabLabel.toLowerCase()} CSV</DialogTitle>
          <DialogDescription>Pick a CSV, then match each column to a field. Same flow as the Voter List CRM.</DialogDescription>
        </DialogHeader>

        {!parse ? (
          <div className="py-8 text-center">
            <input
              type="file"
              accept=".csv,text/csv"
              data-testid={`finances-upload-file-${entity}`}
              onChange={(e) => onPick(e.target.files?.[0])}
              className="mx-auto block file:mr-3 file:py-2 file:px-4 file:rounded file:border file:bg-flag-navy file:text-white file:font-semibold"
            />
            {busy && <p className="text-xs text-slate-500 mt-3">Parsing headers…</p>}
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-slate-500">
              Detected {parse.columns.length} columns. Match each to a destination field, then commit.
            </p>
            <div className="flex items-center gap-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9 w-44" data-testid={`finances-upload-mode-${entity}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">Append (add to existing)</SelectItem>
                  <SelectItem value="replace">Replace (wipe + load)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {targetFields.map((f) => (
                <div key={f.k}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{f.label}</Label>
                  <Select value={mapping[f.k] || "__none"} onValueChange={(v) => setMapping((p) => ({ ...p, [f.k]: v === "__none" ? "" : v }))}>
                    <SelectTrigger className="mt-1 h-10" data-testid={`finances-map-${entity}-${f.k}`}><SelectValue placeholder="— Not mapped —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— Not mapped —</SelectItem>
                      {parse.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {parse.sample?.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Preview (first {parse.sample.length} rows)</p>
                <div className="mt-1 overflow-x-auto border border-slate-200 rounded text-xs">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>{parse.columns.map((c) => <th key={c} className="px-2 py-1 text-left">{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {parse.sample.map((row, i) => (
                        <tr key={`p-${i}`} className="border-t border-slate-100">
                          {row.map((cell, j) => <td key={`p-${i}-c-${j}`} className="px-2 py-1 text-slate-700">{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid={`finances-upload-cancel-${entity}`}>Cancel</Button>
          {parse && (
            <Button onClick={commit} disabled={busy} data-testid={`finances-upload-commit-${entity}`} className="bg-flag-red hover:bg-flag-red-hover text-white font-bold">
              {busy ? "Importing…" : "Import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

